import { useEffect, useState } from 'react';

/**
 * Installing the app on the home screen. Chrome, Edge and Samsung Internet
 * (Android, and desktop Chrome/Edge) offer an install prompt that a page may
 * open itself: one tap and it is installed. iPhone and iPad never let a page
 * do that, in any browser; there the player is shown how to add it through
 * the share menu (Safari, and Chrome/Edge since iOS 16.4).
 */

/** The prompt Chrome holds back for the page (not in the TS DOM types). */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

// Registered as the module loads: Chrome may fire this before React mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
}

/** Opened from the home-screen icon (or an installed desktop window). */
export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/** iPhone/iPad; iPadOS reports itself as a Mac with a touch screen. */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
};

/** Instagram, Facebook, Messenger, TikTok...: no share menu to add from. */
export const isInAppBrowser = () =>
  typeof navigator !== 'undefined' &&
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok|Snapchat|Twitter|LinkedInApp|GSA\//.test(
    navigator.userAgent
  );

export type InstallKind =
  /** The browser's own install dialog, opened by our button. */
  | 'prompt'
  /** iPhone/iPad: steps through the share menu. */
  | 'ios'
  /** iPhone/iPad inside another app's browser: open in Safari first. */
  | 'ios-in-app'
  /** Already installed, or a browser that cannot install. */
  | null;

const kindNow = (): InstallKind => {
  if (installed || isStandalone()) return null;
  if (deferred) return 'prompt';
  if (isIOS()) return isInAppBrowser() ? 'ios-in-app' : 'ios';
  return null;
};

/**
 * Opens the browser's install dialog. Resolves true when the player
 * accepted; the prompt can be used once, so it is dropped either way.
 */
export const promptInstall = async (): Promise<boolean> => {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  notify();
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') installed = true;
  notify();
  return outcome === 'accepted';
};

/** How this device can install the app, kept up to date. */
export const useInstallKind = (): InstallKind => {
  const [kind, setKind] = useState<InstallKind>(kindNow);
  useEffect(() => {
    const update = () => setKind(kindNow());
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return kind;
};
